import {
  forwardRef,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as RadioPrimitive from '@radix-ui/react-radio-group';
import { AlertCircle, Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '../lib/cn';
import { controlClasses, FieldContext, useFieldContext } from './field-context';

/**
 * Form controls.
 *
 * Every control is wrapped by `FormField`, which owns the label, the required
 * marker, the description and the error — so a field can never ship without a
 * label, and an error message is always associated with its input through
 * `aria-describedby` rather than merely sitting near it (§54).
 */

export function FormField({
  label,
  description,
  error,
  required,
  hint,
  children,
  className,
}: {
  label: string;
  description?: ReactNode;
  error?: string;
  required?: boolean;
  /** Trailing hint, e.g. a character count or a computed value. */
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const generated = useId();
  const labelId = `${generated}-label`;
  const descriptionId = description ? `${generated}-description` : undefined;
  const errorId = error ? `${generated}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <FieldContext.Provider value={{ id: generated, describedBy, invalid: Boolean(error), labelId }}>
      <div className={cn('min-w-0', className)}>
        <div className="mb-1.5 flex items-baseline justify-between gap-3">
          <label id={labelId} htmlFor={generated} className="text-base font-medium text-ink">
            {label}
            {required ? (
              <span className="ml-0.5 text-danger" aria-hidden>
                *
              </span>
            ) : null}
            {required ? <span className="sr-only"> (required)</span> : null}
          </label>
          {hint ? <span className="shrink-0 text-caption text-ink-muted">{hint}</span> : null}
        </div>
        {description ? (
          <p id={descriptionId} className="mb-1.5 text-sm text-ink-muted">
            {description}
          </p>
        ) : null}
        {children}
        {error ? (
          <p
            id={errorId}
            role="alert"
            className="mt-1.5 flex items-center gap-1.5 text-sm text-danger"
          >
            <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className, ...props }, ref) {
    const field = useFieldContext();
    return (
      <input
        ref={ref}
        id={field.id || props.id}
        aria-describedby={field.describedBy}
        aria-invalid={field.invalid || undefined}
        className={cn(
          controlClasses,
          'h-9',
          field.invalid
            ? 'border-danger focus:ring-danger/25'
            : 'border-line-strong focus:border-brand',
          className,
        )}
        {...props}
      />
    );
  },
);

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function TextArea({ className, rows = 4, ...props }, ref) {
  const field = useFieldContext();
  return (
    <textarea
      ref={ref}
      id={field.id || props.id}
      rows={rows}
      aria-describedby={field.describedBy}
      aria-invalid={field.invalid || undefined}
      className={cn(
        controlClasses,
        'resize-y py-2 leading-relaxed',
        field.invalid
          ? 'border-danger focus:ring-danger/25'
          : 'border-line-strong focus:border-brand',
        className,
      )}
      {...props}
    />
  );
});

/** Native select — used where the option list is short and known. */
export const NativeSelect = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function NativeSelect({ className, children, ...props }, ref) {
    const field = useFieldContext();
    return (
      <div className="relative">
        <select
          ref={ref}
          id={field.id || props.id}
          aria-describedby={field.describedBy}
          aria-invalid={field.invalid || undefined}
          className={cn(
            controlClasses,
            'h-9 appearance-none pr-9',
            field.invalid ? 'border-danger' : 'border-line-strong focus:border-brand',
            className,
          )}
          {...props}
        >
          {children}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
          aria-hidden
        />
      </div>
    );
  },
);

/* -------------------------------------------------------------------------- */
/* Radix select — used where options need icons, groups or descriptions        */
/* -------------------------------------------------------------------------- */

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export function Select({
  value,
  onValueChange,
  onBlur,
  options,
  placeholder = 'Select…',
  disabled,
  className,
  ariaLabel,
}: {
  value: string | undefined;
  onValueChange: (value: string) => void;
  /**
   * Called when the control is done being interacted with.
   *
   * A form validating on blur needs this: without it, a react-hook-form
   * `Controller` around this Select never learns the field was touched, and a
   * required-but-empty dropdown stays silent until submit. Radix closes the
   * popover back onto the trigger, so the trigger's own blur fires while the
   * list is open too — hence it is reported on close and on genuine blur, not
   * on every focus change.
   */
  onBlur?: () => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}) {
  const field = useFieldContext();
  return (
    <SelectPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      onOpenChange={(open) => {
        if (!open) onBlur?.();
      }}
    >
      <SelectPrimitive.Trigger
        id={field.id || undefined}
        onBlur={() => onBlur?.()}
        aria-label={ariaLabel}
        aria-describedby={field.describedBy}
        aria-invalid={field.invalid || undefined}
        className={cn(
          controlClasses,
          'flex h-9 items-center justify-between gap-2 text-left',
          field.invalid ? 'border-danger' : 'border-line-strong focus:border-brand',
          className,
        )}
      >
        <SelectPrimitive.Value
          placeholder={<span className="text-ink-subtle">{placeholder}</span>}
        />
        <SelectPrimitive.Icon>
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-50 max-h-72 w-[var(--radix-select-trigger-width)] animate-fade-in overflow-hidden rounded-lg border border-line bg-surface shadow-lg"
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'flex cursor-pointer select-none items-start gap-2 rounded px-2 py-1.5 text-base text-ink outline-none',
                  'data-[highlighted]:bg-neutral-soft data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                )}
              >
                <SelectPrimitive.ItemIndicator className="mt-0.5 shrink-0">
                  <Check className="h-3.5 w-3.5 text-brand" aria-hidden />
                </SelectPrimitive.ItemIndicator>
                <span className={cn('min-w-0', 'data-[state=checked]:font-medium')}>
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                  {option.description ? (
                    <span className="mt-0.5 block text-caption text-ink-muted">
                      {option.description}
                    </span>
                  ) : null}
                </span>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

/* -------------------------------------------------------------------------- */
/* Toggle controls                                                             */
/* -------------------------------------------------------------------------- */

export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div className="min-w-0">
        <label htmlFor={id} className="text-base font-medium text-ink">
          {label}
        </label>
        {description ? <p className="mt-0.5 text-sm text-ink-muted">{description}</p> : null}
      </div>
      <SwitchPrimitive.Root
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className={cn(
          'relative h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[state=checked]:bg-brand data-[state=unchecked]:bg-line-strong',
        )}
      >
        <SwitchPrimitive.Thumb className="block h-4 w-4 rounded-full bg-white shadow-sm transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0" />
      </SwitchPrimitive.Root>
    </div>
  );
}

export function Checkbox({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  indeterminate,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: string;
  disabled?: boolean;
  indeterminate?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-2.5">
      <CheckboxPrimitive.Root
        id={id}
        checked={indeterminate ? 'indeterminate' : checked}
        onCheckedChange={(next) => onCheckedChange(next === true)}
        disabled={disabled}
        className={cn(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[state=unchecked]:border-line-strong data-[state=unchecked]:bg-surface',
          'data-[state=checked]:border-brand data-[state=checked]:bg-brand',
          'data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand',
        )}
      >
        <CheckboxPrimitive.Indicator className="text-white">
          {indeterminate ? (
            <span className="block h-0.5 w-2 rounded bg-white" />
          ) : (
            <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label ? (
        <div className="min-w-0">
          <label htmlFor={id} className="cursor-pointer text-base text-ink">
            {label}
          </label>
          {description ? <p className="mt-0.5 text-sm text-ink-muted">{description}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

export function RadioGroup({
  value,
  onValueChange,
  options,
  ariaLabel,
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  ariaLabel: string;
}) {
  return (
    <RadioPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      aria-label={ariaLabel}
      className="space-y-2"
    >
      {options.map((option) => (
        <label
          key={option.value}
          className={cn(
            'flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 transition-colors',
            value === option.value
              ? 'border-brand bg-brand-soft'
              : 'border-line bg-surface hover:bg-surface-muted',
          )}
        >
          <RadioPrimitive.Item
            value={option.value}
            disabled={option.disabled}
            className={cn(
              'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
              'data-[state=checked]:border-brand data-[state=unchecked]:border-line-strong',
            )}
          >
            <RadioPrimitive.Indicator className="h-2 w-2 rounded-full bg-brand" />
          </RadioPrimitive.Item>
          <span className="min-w-0">
            <span className="block text-base font-medium text-ink">{option.label}</span>
            {option.description ? (
              <span className="mt-0.5 block text-sm text-ink-muted">{option.description}</span>
            ) : null}
          </span>
        </label>
      ))}
    </RadioPrimitive.Root>
  );
}

/* -------------------------------------------------------------------------- */
/* Search                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The search field.
 *
 * `type="search"` is not used, because WebKit draws its own clear button inside
 * it — a grey circle that ignores every token here and sits at a different
 * inset to our own icons. The input is a plain text box carrying
 * `role="searchbox"`, so assistive technology hears exactly what it heard
 * before, and the clear affordance is ours.
 */
export const SearchInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { onClear?: () => void }
>(function SearchInput({ className, onClear, value, onChange, ...props }, ref) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** Only consulted when the field is uncontrolled. */
  const [typed, setTyped] = useState(false);

  const controlled = value !== undefined && value !== null;
  const hasText = controlled ? String(value).length > 0 : typed;

  const clear = (): void => {
    const input = inputRef.current;
    if (input) {
      // React installs its own setter on the input's `value`, so assigning to
      // it directly would update the DOM without telling React — the caller's
      // `onChange` would never fire and a controlled field would spring back.
      // Going through the prototype setter and dispatching the event is what
      // makes clearing indistinguishable from deleting the text by hand.
      const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
      setter?.call(input, '');
      input.dispatchEvent(new Event('input', { bubbles: true }));
      // The field keeps focus, so the next search starts without a click.
      input.focus();
    }
    setTyped(false);
    onClear?.();
  };

  return (
    <div className="relative">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
        aria-hidden
      />
      <input
        ref={(node) => {
          inputRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        type="text"
        role="searchbox"
        autoComplete="off"
        value={value}
        onChange={(event) => {
          if (!controlled) setTyped(event.target.value.length > 0);
          onChange?.(event);
        }}
        className={cn(
          controlClasses,
          'h-9 border-line-strong pl-8 focus:border-brand',
          hasText ? 'pr-8' : undefined,
          className,
        )}
        {...props}
      />
      {hasText && !props.disabled ? (
        <button
          type="button"
          onClick={clear}
          aria-label="Clear search"
          className={cn(
            'absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded',
            'text-ink-muted transition-colors hover:bg-neutral-soft hover:text-ink',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
          )}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
});
